// src/components/planner/components/WeekGrid.tsx

import React, { useState } from 'react';
import { Pencil, Calendar } from 'lucide-react';
import EditEventModal from '../modals/EditEventModal';

interface WeekGridProps {
    events: any[];
    onReload: () => void;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const EVENT_TYPE_MAP: Record<string, string> = {
    // Aulas & Cursos
    class: 'Aula', classes: 'Aula',
    course: 'Curso',
    tutoring: 'Monitoria',
    office_hours: 'Plantão de Dúvidas',
    // Estudo
    individual_study: 'Estudo Individual',
    study: 'Estudo',
    review: 'Revisão',
    mock_exam: 'Simulado',
    questions: 'Questões',
    study_group: 'Grupo de Estudo',
    research: 'Pesquisa',
    reading: 'Leitura',
    // Trabalho & Projetos
    work: 'Trabalho',
    project: 'Projeto',
    meeting: 'Reunião',
    planning: 'Planejamento',
    organization: 'Organização',
    // Pessoal
    gym: 'Academia',
    meditation: 'Meditação',
    rest: 'Descanso',
    family: 'Compromisso Familiar',
    social: 'Evento Social',
    appointment: 'Atendimento',
    // Outros legados
    sport: 'Esporte', sports: 'Esporte',
    personal: 'Pessoal', health: 'Saúde',
    leisure: 'Lazer', other: 'Outro',
};

function translateType(type?: string, customType?: string): string | undefined {
    if (!type) return undefined;
    if (type === 'other' && customType) return customType;
    return EVENT_TYPE_MAP[type.toLowerCase()] ?? type;
}

const WeekGrid: React.FC<WeekGridProps> = ({ events, onReload, onShowToast }) => {
    const getDayName = (dayOfWeek: number): string => {
        const days = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
        return days[dayOfWeek];
    };

    const getDateForDay = (dayOfWeek: number): string => {
        const today = new Date();
        const currentDay = today.getDay();
        const diff = dayOfWeek - currentDay;
        const targetDate = new Date(today);
        targetDate.setDate(today.getDate() + diff);

        return targetDate.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit'
        });
    };

    const [editingEvent, setEditingEvent] = useState<any | null>(null);

    const formatTimeSlot = (time: string): string => time.substring(0, 5);

    return (
        <div className="plnr-wg-grid">
            {[0, 1, 2, 3, 4, 5, 6].map(day => (
                <div key={day} className="plnr-wg-day">
                    <div className="plnr-wg-day-header">
                        <h4>{getDayName(day)}</h4>
                        <span className="plnr-wg-day-date">{getDateForDay(day)}</span>
                    </div>
                    <div className="plnr-wg-events">
                        {events
                            .filter(e => e.dayOfWeek === day)
                            .sort((a, b) => a.startTime.localeCompare(b.startTime))
                            .map(event => (
                                <div
                                    key={event._id}
                                    className="plnr-wg-event"
                                    style={{ borderLeftColor: event.color }}
                                >
                                    {/* NOVO - mostra startTime → endTime */}
                                    <div className="plnr-wg-event-time">
                                        {formatTimeSlot(event.startTime)}
                                        {event.endTime && ` → ${formatTimeSlot(event.endTime)}`}
                                    </div>
                                    <div className="plnr-wg-event-content">
                                        <h5>{event.title}</h5>
                                        {event.location && (
                                            <span className="plnr-wg-event-location">
                                                {event.location}
                                            </span>
                                        )}
                                        {event.type && (
                                            <span className="plnr-wg-event-badge">{translateType(event.type, event.customType)}</span>
                                        )}
                                    </div>
                                    <button
                                        className="plnr-wg-event-delete"
                                        onClick={() => setEditingEvent(event)}
                                    >
                                        <Pencil size={14} />
                                    </button>
                                </div>
                            ))
                        }
                        {events.filter(e => e.dayOfWeek === day).length === 0 && (
                            <div className="plnr-wg-empty">
                                <Calendar size={20} />
                                <span>Livre</span>
                            </div>
                        )}
                    </div>
                </div>
            ))}

            {editingEvent && (
                <EditEventModal
                    event={editingEvent}
                    onClose={() => setEditingEvent(null)}
                    onSave={() => { setEditingEvent(null); onReload(); }}
                    onShowToast={onShowToast}
                />
            )}
        </div>
    );
};

export default WeekGrid;